/**
 * Unit photo review (MERCH-59): how each photo in the unit drawer is labelled.
 * An assistant's upload (provenance 'mcp') waits as 'pending' until a person
 * approves it; only approved photos count toward publishing.
 *
 * Illustrative images (MERCH-77, D91): a generated, AI-made or stock image kept as its
 * own kind. It never counts toward publishing, needs alt text, has a per-unit cap (from the
 * server), and shoppers see it only on the product page, labelled
 * "Illustrative image — not this unit". The server enforces every rule here.
 */
const LABELS = {
  pending: (p) => (p.provenance === 'mcp' ? 'MCP upload — pending review' : 'Pending review'),
  rejected: () => 'Rejected',
  approved: () => null,
};
const VARIANTS = { pending: 'warning', rejected: 'error', approved: 'success' };

/** { label, variant } for a photo's badge, or null when it needs none (approved). */
export function photoBadge(photo) {
  const label = (LABELS[photo.review_status] || (() => photo.review_status))(photo);
  return label ? { label, variant: VARIANTS[photo.review_status] || 'default' } : null;
}

/** Whether the signed-in person may decide on this photo now (the server checks again). */
export const canReview = (photo, can, perm) => photo.review_status === 'pending' && can(perm[0], perm[1]);

/** lib/media/upload MAX_ALT: the longest alt text a file keeps. */
export const MAX_ALT = 300;

/**
 * The unit file kinds, in drawer order (a registry: a new kind is an entry here). Copy and
 * form flags only: the rules (the cap, whether alt text is required) come from the server's
 * `kinds` in the unit's photo list (lib/media/manage kindRules), so they are never restated here.
 *   title(rules)        the section heading      empty     the section with no files
 *   option / noun       the kind picker label / the word in toasts and dialogs
 *   hint                the line under the picker
 *   altLabel            the alt text field's label
 *   multiple            whether several files may be picked at once
 *   added / approved    toast bodies after an upload / an approval
 *   rejectTitle         the reject dialog's title
 *   limitMessage(max, existing)  why one more cannot be added
 *   altRequired         the alt rule the intake wizard checks before the unit (and so the server's rules) exists
 */
export const UNIT_FILE_KINDS = {
  photo: {
    title: () => 'Unit photos (count toward publishing)',
    option: 'Unit photo',
    noun: 'Photo',
    empty: 'No photos of this unit yet.',
    hint: 'A genuine photo of this exact unit, taken by a person.',
    altLabel: 'What the photo shows (optional)',
    multiple: true,
    altRequired: false,
    added: 'It counts toward publishing.',
    approved: 'It now counts toward publishing.',
    rejectTitle: 'Reject this photo?',
    limitMessage: (max, existing) => `At most ${max} photos per unit (${existing} already added).`,
  },
  illustrative: {
    title: (rules = {}) => `Illustrative images (not counted${rules.max ? `, max ${rules.max}` : ''})`,
    option: 'Illustrative image',
    noun: 'Illustrative image',
    empty: 'No illustrative images. Generated, AI-made or stock images go here, never as unit photos.',
    hint: 'A generated, AI-made or stock image. It never counts toward publishing and is labelled "not this unit" for shoppers.',
    altLabel: 'What the image shows (alt text)',
    multiple: false,
    altRequired: true,
    added: 'Shoppers see it on the product page, labelled. It does not count toward publishing.',
    approved: 'Shoppers will see it on the product page, labelled "Illustrative image — not this unit". It does not count toward publishing.',
    rejectTitle: 'Reject this illustrative image?',
    limitMessage: (max, existing) => `At most ${max} illustrative images per unit (${existing} already added).`,
  },
};
export const UNIT_KIND_KEYS = Object.keys(UNIT_FILE_KINDS);

/**
 * A product model's file kinds (MERCH-81, D95), the same shape as UNIT_FILE_KINDS: catalog images
 * (manufacturer, stock or AI) shared by every unit of the model, never counted, always labelled.
 */
export const MODEL_FILE_KINDS = {
  catalog: {
    title: (rules = {}) => `Catalog images (not counted${rules.max ? `, max ${rules.max}` : ''})`,
    option: 'Catalog image',
    noun: 'Catalog image',
    empty: 'No catalog images. Manufacturer, stock or AI images of this model go here; every unit of it shows them, labelled.',
    hint: 'A manufacturer, stock or AI image of this model. Shoppers see it on every unit of the model, labelled "Catalog image — not this unit", and as the card cover until the unit has a photo. It never counts toward publishing.',
    altLabel: 'What the image shows (alt text)',
    multiple: false,
    altRequired: true,
    added: 'Shoppers see it on this model\'s units, labelled. It does not count toward publishing.',
    approved: 'Shoppers will see it on this model\'s units, labelled "Catalog image — not this unit". It does not count toward publishing.',
    rejectTitle: 'Reject this catalog image?',
    limitMessage: (max, existing) => `At most ${max} catalog images per model (${existing} already added).`,
  },
};

const keysOf = (kinds) => Object.keys(kinds);
const kindOf = (f, kinds) => (f && Object.prototype.hasOwnProperty.call(kinds, f.kind) ? f.kind : keysOf(kinds)[0]);
export const fileKind = (f, kinds) => kinds[kindOf(f, kinds)];

/** The record's files grouped by kind ({ photo: [...], illustrative: [...] }), order kept. */
export function filesByKind(files, kinds) {
  const out = Object.fromEntries(keysOf(kinds).map((k) => [k, []]));
  for (const f of files) out[kindOf(f, kinds)].push(f);
  return out;
}

/** The approve/reject toast for a file: { title, body }. */
export function reviewToast(file, decision, kinds) {
  const k = fileKind(file, kinds);
  if (decision === 'approve') return { title: `${k.noun} approved`, body: k.approved };
  return { title: `${k.noun} rejected`, body: 'It stays hidden from shoppers.' };
}

/** The "n approved · n pending review · n rejected" line for one kind's counts. */
export function countsLine(c = {}) {
  return `${c.approved || 0} approved${c.pending ? ` · ${c.pending} pending review` : ''}${c.rejected ? ` · ${c.rejected} rejected` : ''}`;
}

/**
 * Why adding `count` more files of `kind` cannot be sent, or '' when it can. `rules` is the
 * server's entry for the kind ({ max, alt_required }); without it (the intake wizard, before the
 * unit exists) only the alt text is checked here and the server refuses one over the cap.
 * `existing` is how many of that kind the unit already has (removed ones are not listed).
 */
export function uploadProblem(kind, { count = 1, existing = 0, alt = '', rules } = {}, kinds = UNIT_FILE_KINDS) {
  const k = kinds[kind];
  if (!k) return 'Pick a kind of image';
  const altRequired = rules ? rules.alt_required : k.altRequired;
  if (rules && rules.max && existing + count > rules.max) return k.limitMessage(rules.max, existing);
  if (altRequired && count > 0 && !String(alt || '').trim()) return `Describe what the ${k.noun.toLowerCase()} shows (alt text).`;
  return '';
}

/** The alt text of file `i` of `n` sent with one shared description: numbered when there are several. */
export const altFor = (description, i, n) => (n > 1 ? `${String(description).trim()} (${i + 1})` : String(description).trim());
